import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography, Grid, Button, IconButton, Hidden, Divider, CircularProgress,
} from '@material-ui/core';
import clsx from 'clsx';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import EditIcon from '@material-ui/icons/Edit';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import PublishIcon from '@material-ui/icons/Publish';
import AttachmentIcon from '@material-ui/icons/AttachFile';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { Translate } from 'react-localize-redux';
import SaveIcon from '@material-ui/icons/Save';
import AddIcon from '@material-ui/icons/Add';
import DoneIcon from '@material-ui/icons/Done';
import Notification from '@trdk/style/NotificationBar';
import Box from '@material-ui/core/Box';
import { doFetch } from '@trdk/style/Redux/fetch.actions';
import { submit } from '../components/submit/submit.actions';
import TitleField from '../components/title-field';
import InfoCard from '../components/info-card';
import StyledTable from '../components/table/styled-table';
import CustomTableRow from '../components/table/custom-table-row';
import CustomTableCell from '../components/table/custom-table-cell';
import ListMobile from '../components/list-mobile';
import ListValues from '../components/list-values';
import {
  getElementBySysId, getControllersListCommaSep, formatDate,
  getLicenseListFromLicenseSysIDs, getViolationSeverityStr,
  getControlStatusString, CONTROL_STATUS_STARTED, sortByNewestStartTime,
  sortByNewestStart, getCurrentLocalDateTimeStr, dateTimeToString,
  convertDateStrToUseSlash, getLocationNameFromLicenseList,
} from '../utils';
import LicenseList from '../components/license-list';
import { createOrUpdateWorkPlan, updateWorkSession, createReceiptFileUpload } from '../services/submit/submit-servce';
import { fetchAssignments } from '../services/fetch/load-form-service';
import NotificationBox from '../components/notification-box';

function findActiveWorkSession(workSessions) {
  let match = null;
  workSessions.forEach((workSession) => {
    if (workSession.end == null || workSession.end === '') {
      match = workSession;
    }
  });
  return match;
}

function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

function areDatesDifferent(initialDate, newDate) {
  if (!isValidDate(newDate)) return false;

  return (initialDate == null || new Date(
    convertDateStrToUseSlash(initialDate),
  ).toISOString() !== newDate.toISOString());
}

class WorkPlan extends Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      submitted: false,
      submitError: false,
      fileErrors: [],
    };
  }

  uploadFile = (event) => {
    const { dispatch, match: { params: { workPlanID } } } = this.props;
    const { fileErrors } = this.state;
    const { files } = event.target;
    const file = files[files.length - 1];

    createReceiptFileUpload(file, file.name, file.type, workPlanID).then((res) => {
      if (!res.ok) { throw res; }
      return res;
    }).then(() => {
      dispatch(doFetch('ASSIGNMENTS', fetchAssignments().then((assignmentsRes) => {
        if (!assignmentsRes.ok) { throw assignmentsRes; }
        return assignmentsRes.json();
      })));
    }).catch(() => this.setState({ fileErrors: [...fileErrors, 'Feil oppstod under opplasting. Prøv igjen'] }));
  }

  render() {
    const {
      submitting, submitted, submitError, fileErrors,
    } = this.state;

    const {
      dispatch, classes, history, match: { params: { assignmentID, workPlanID } },
      assignments: { payload: assignmentsPayload },
      licenses: { payload: licensesPayload },
      activeWorkPlan,
    } = this.props;

    const activeWorkPlanId = activeWorkPlan ? activeWorkPlan.sysId : '';
    const assignment = getElementBySysId(assignmentsPayload, assignmentID);
    if (assignment == null) {
      return (
        <div>
          {`Ingen kontrolloppdrag funnet med sysId: ${assignmentID}`}
        </div>
      );
    }

    const workPlan = getElementBySysId(assignment.workPlans, workPlanID);
    const licenses = getLicenseListFromLicenseSysIDs(licensesPayload, workPlan.licensesSysId);

    if (workPlan.workSessions == null) workPlan.workSessions = [];
    workPlan.workSessions.forEach((workSession, idx) => {
      if (workSession.start != null) {
        workPlan.workSessions[idx].start = new Date(convertDateStrToUseSlash(workSession.start));
      }
      if (workSession.end != null) {
        workPlan.workSessions[idx].end = new Date(convertDateStrToUseSlash(workSession.end));
      }
    });

    if (workPlan == null) {
      return (
        <div>
          {`Ingen arbeidsplan funnet med sysId: ${workPlanID}`}
        </div>
      );
    }

    if (submitting && !submitted) {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    }

    const activeWorkSession = findActiveWorkSession(workPlan.workSessions);

    return (
      <div>
        <TitleField
          title={`Arbeidsplan ${formatDate(workPlan.date)}`}
        />

        {submitted && submitError && (
          <NotificationBox
            variant="error"
            header="En uventet feil oppsto under kommunikasjon med server. Vennligst prøv igjen."
            showIcon
            margin="2rem 0"
          />
        )}

        <InfoCard elevation={2} className={classes.card}>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <Typography variant="h6" color="primary">
                Opplysninger om arbeidsplanen
              </Typography>
            </Grid>
            <Grid item xs={2} className={classes.editBtnWrapper}>
              <IconButton
                onClick={() => {
                  history.push(`/oppdrag--${assignmentID}/arbeidsplan--${workPlanID}/rediger`);
                }}
              >
                <EditIcon />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              <ListValues values={{
                date: formatDate(workPlan.date),
                controllers: getControllersListCommaSep(workPlan.controllers),
                numberOfLicenses: licenses.length,
              }}
              />
            </Grid>
          </Grid>
        </InfoCard>

        <div className={classes.btnBetweenCards}>
          <Button
            variant="contained"
            color="primary"
            disabled={activeWorkPlanId === workPlanID}
            onClick={() => {
              dispatch(submit({ sysId: workPlanID }, 'activeWorkPlan'));
              dispatch(submit({ showWorkPlanLicenses: true }, 'mapOptions'));
            }}
          >
            Sett som aktiv arbeidsplan
          </Button>
          {activeWorkPlanId === workPlanID && (
            <Typography>
              Denne arbeidsplanen er satt som aktiv.
            </Typography>
          )}
        </div>

        <InfoCard elevation={2} className={classes.card}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary">
                Arbeidsøkter
              </Typography>

              <Formik
                initialValues={{
                  workSessions: workPlan.workSessions != null ? workPlan.workSessions : [],
                }}
              >

                {({
                  values, setFieldValue,
                }) => (
                  <Form>
                    {activeWorkSession == null && (
                      <Button
                        variant="outlined"
                        color="primary"
                        className={classes.workSessionBtn}
                        startIcon={<AddIcon />}
                        onClick={() => {
                          this.setState({ submitted: false });
                          this.setState({ submitting: true });
                          const payload = {
                            sysId: workPlanID,
                            workSessionStart: getCurrentLocalDateTimeStr(),
                          };
                          createOrUpdateWorkPlan(payload, 'PUT').then((res) => {
                            this.setState({ submitting: false });
                            this.setState({ submitted: true });
                            if (!res.ok) {
                              this.setState({ submitError: true });
                              throw res;
                            }

                            dispatch(doFetch('ASSIGNMENTS', fetchAssignments().then((assignmentsRes) => {
                              if (!assignmentsRes.ok) { throw assignmentsRes; }
                              return assignmentsRes.json();
                            })));
                          });
                        }}
                      >
                        Start ny arbeidsøkt
                      </Button>
                    )}
                    {activeWorkSession != null && (
                      <>
                        <Button
                          variant="outlined"
                          color="primary"
                          className={classes.workSessionBtn}
                          startIcon={<DoneIcon />}
                          onClick={() => {
                            this.setState({ submitted: false });
                            this.setState({ submitting: true });
                            const payload = {
                              sysId: workPlanID,
                              workSessionEnd: getCurrentLocalDateTimeStr(),
                            };
                            createOrUpdateWorkPlan(payload, 'PUT').then((res) => {
                              this.setState({ submitting: false });
                              this.setState({ submitted: true });
                              if (!res.ok) {
                                this.setState({ submitError: true });
                                throw res;
                              }

                              dispatch(doFetch('ASSIGNMENTS', fetchAssignments().then((assignmentsRes) => {
                                if (!assignmentsRes.ok) { throw assignmentsRes; }
                                return assignmentsRes.json();
                              })));
                            });
                          }}
                        >
                          Avslutt aktiv arbeidsøkt
                        </Button>
                        <br />
                        <Typography variant="caption">
                          Setter nåværende tidspunkt som slutt-tid for aktiv arbeidsøkt.
                        </Typography>
                      </>
                    )}
                    {values.workSessions && values.workSessions.length > 0 && (
                      <Grid container spacing={2} className={classes.inputGrid}>
                        {values.workSessions.sort(sortByNewestStart).map((workSession, index) => {
                          const startField = `workSessions[${index}].start`;
                          const endField = `workSessions[${index}].end`;

                          const initialValueStart = workPlan.workSessions[index].start;
                          const initialValueEnd = workPlan.workSessions[index].end;
                          const diffStart = areDatesDifferent(initialValueStart, workSession.start);
                          const diffEnd = areDatesDifferent(initialValueEnd, workSession.end);
                          return [
                            <Grid item xs={12} sm={6} md={4} key={`start--${workSession.sysId}`}>
                              <KeyboardDateTimePicker
                                ampm={false}
                                value={workSession.start}
                                maxDate={workSession.end != null
                                  ? workSession.end : new Date('2100/01/01')}
                                onChange={(e) => {
                                  setFieldValue(startField, e);
                                }}
                                showTodayButton
                                todayLabel="I dag"
                                cancelLabel="Avbryt"
                                invalidDateMessage="Ugyldig dato"
                                maxDateMessage="Kan ikke være etter slutt-tiden!"
                                format="dd.MM.yyyy HH:mm"
                                disabled={activeWorkSession != null && workSession.end == null}
                                label={<Translate id="workSession.startTime" />}
                              />
                            </Grid>,
                            <Grid item xs={12} sm={6} md={4} key={`end--${workSession.sysId}`}>
                              <KeyboardDateTimePicker
                                ampm={false}
                                value={workSession.end}
                                minDate={workSession.start}
                                onChange={(e) => {
                                  setFieldValue(endField, e);
                                }}
                                showTodayButton
                                todayLabel="I dag"
                                cancelLabel="Avbryt"
                                invalidDateMessage="Ugyldig dato"
                                minDateMessage="Kan ikke være før start-tiden!"
                                format="dd.MM.yyyy HH:mm"
                                label={<Translate id="workSession.endTime" />}
                              />
                            </Grid>,
                            <Grid item xs={12} md={4} key={`button--${workSession.sysId}`}>
                              {(diffStart || diffEnd) && workSession.start <= workSession.end && (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  className={classes.saveBtn}
                                  startIcon={<SaveIcon />}
                                  onClick={() => {
                                    const payload = {
                                      sysId: workSession.sysId,
                                      start: diffStart
                                        ? dateTimeToString(workSession.start, true) : null,
                                      end: diffEnd
                                        ? dateTimeToString(workSession.end, true) : null,
                                    };
                                    updateWorkSession(payload).then((res) => {
                                      this.setState({ submitting: false });
                                      this.setState({ submitted: true });
                                      if (!res.ok) {
                                        this.setState({ submitError: true });
                                        throw res;
                                      }

                                      dispatch(doFetch('ASSIGNMENTS', fetchAssignments().then((assignmentsRes) => {
                                        if (!assignmentsRes.ok) { throw assignmentsRes; }
                                        return assignmentsRes.json();
                                      })));
                                    });
                                  }}
                                >
                                  Lagre
                                </Button>
                              )}
                            </Grid>,
                            <Grid item xs={12} key={`divider--${workSession.sysId}`}>
                              <Divider />
                            </Grid>,
                          ];
                        })}
                      </Grid>
                    )}
                  </Form>
                )}
              </Formik>
            </Grid>
          </Grid>
        </InfoCard>

        <InfoCard elevation={2} className={classes.card}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary">
                Kvitteringer
              </Typography>
              <Box display="flex" justifyContent="space-between" maxWidth="20rem">
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.workSessionBtn}
                  startIcon={<CameraAltIcon />}
                  component="label"
                >
                  Ta bilde
                  <input
                    type="file"
                    accept="image/*"
                    capture="camera"
                    hidden
                    onChange={this.uploadFile}
                  />
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.workSessionBtn}
                  startIcon={<PublishIcon />}
                  component="label"
                >
                  Last opp bilde
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={this.uploadFile}
                  />
                </Button>
              </Box>
              {fileErrors.map(fileError => <Notification variant="error" message={fileError} />)}
              {/* <input type="file" accept="image/*" capture="camera" /> */}
              {workPlan.receipts && workPlan.receipts.length > 0 && (
                <Grid container spacing={2} className={classes.inputGrid}>
                  <List component="nav" aria-label="main mailbox folders">
                    {workPlan.receipts.map(receipt => [
                      <ListItem
                        button
                        component="a"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={receipt.url}
                        key={`receiptAtc-${receipt.sysId}`}
                      >
                        <ListItemIcon>
                          <AttachmentIcon />
                        </ListItemIcon>
                        <ListItemText primary={receipt.fileName} />
                      </ListItem>,
                    ])}
                  </List>
                </Grid>
              )}
            </Grid>
          </Grid>
        </InfoCard>

        <InfoCard elevation={2} className={classes.card}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary">Kontroller</Typography>
              {(!workPlan.controls || workPlan.controls.length === 0) && (
                <Typography variant="subtitle1">Ingen</Typography>
              )}
              {workPlan.controls && workPlan.controls.length > 0 && (
                <>
                  <Hidden lgUp>
                    <ListMobile
                      fetched
                      rowsPerPage={5}
                      content={workPlan.controls.sort(sortByNewestStartTime).map(control => ({
                        sysId: control.sysId,
                        primary: formatDate(control.startTime),
                        secondary: (
                          <>
                            <Typography component="span" variant="body1" display="block">
                              {`Sted: ${getLocationNameFromLicenseList(licenses, control.licenseSysId)}`}
                            </Typography>
                            <Typography component="span" variant="body1" display="block">
                              {`Status: ${getControlStatusString(control.status)}`}
                            </Typography>
                            {control.status !== CONTROL_STATUS_STARTED && (
                              <Typography component="span" variant="body1" display="block">
                                Brudd:
                                <span className={
                                  clsx(control.violationsSeverity !== 0 ? classes.red : null)
                                }
                                >
                                  {` ${getViolationSeverityStr(control.violationsSeverity)}`}
                                </span>
                              </Typography>
                            )}
                          </>
                        ),
                        onClick: () => {
                          history.push(`/${control.licenseSysId}/kontroll--${control.sysId}`);
                        },
                      }))}
                    />
                  </Hidden>
                  <Hidden mdDown>
                    <StyledTable
                      header={[
                        'Dato',
                        'Sted',
                        'Status',
                        'Brudd',
                        '',
                      ]}
                      rowsPerPage={10}
                    >
                      {workPlan.controls.sort(sortByNewestStartTime).map((control, index) => (
                        <CustomTableRow
                          key={control.sysId}
                          onClick={() => {
                            history.push(`/${control.licenseSysId}/kontroll--${control.sysId}`);
                          }}
                          odd={!(index % 2)}
                        >
                          <CustomTableCell>{formatDate(control.startTime)}</CustomTableCell>
                          <CustomTableCell>
                            {getLocationNameFromLicenseList(licenses, control.licenseSysId)}
                          </CustomTableCell>
                          <CustomTableCell>
                            {getControlStatusString(control.status)}
                          </CustomTableCell>
                          <CustomTableCell>
                            {control.status !== CONTROL_STATUS_STARTED && (
                              <span className={
                                clsx(control.violationsSeverity !== 0 ? classes.red : null)
                              }
                              >
                                {getViolationSeverityStr(control.violationsSeverity)}
                              </span>
                            )}
                          </CustomTableCell>
                          <CustomTableCell shrink>
                            <ArrowForwardIcon color="primary" />
                          </CustomTableCell>
                        </CustomTableRow>
                      ))}
                    </StyledTable>
                  </Hidden>
                </>
              )}
            </Grid>
          </Grid>
        </InfoCard>

        <InfoCard elevation={2} className={classes.card}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary">
                Bevillinger
              </Typography>
              <LicenseList
                licenses={licenses}
                showZone={false}
                showIsControlCompleted
                workPlan={workPlan}
              />
            </Grid>
          </Grid>
        </InfoCard>
      </div>
    );
  }
}

const styles = theme => ({
  bold: {
    fontWeight: 'bold',
  },
  card: {
    marginBottom: '1rem',
  },
  flex: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  paper: {
    padding: '0.8rem',
    paddingBottom: 0,
    border: '1px solid lightgray',
  },
  margin: {
    margin: '1rem 0',
  },
  marginTop: {
    marginTop: '3rem',
  },
  btnBetweenCards: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
  },
  workSessionBtn: {
    marginTop: theme.spacing(3),
  },
  saveBtn: {
    margin: theme.spacing(1),
  },
  editBtnWrapper: {
    textAlign: 'right',
  },
  red: {
    color: 'red',
  },
  inputGrid: {
    marginTop: theme.spacing(2),
  },
  loading: {
    margin: theme.spacing(5),
    textAlign: 'center',
  },
});

WorkPlan.propTypes = {
  match: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  assignments: PropTypes.object.isRequired,
  licenses: PropTypes.object.isRequired,
  activeWorkPlan: PropTypes.object,
};

WorkPlan.defaultProps = {
  activeWorkPlan: null,
};

const mapStateToProps = ({
  licenses,
  assignments,
  form: {
    activeWorkPlan,
  },
}) => ({
  licenses,
  assignments,
  activeWorkPlan,
});

export default connect(mapStateToProps)(withRouter(withStyles(styles)(WorkPlan)));
